import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, showApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TestTube, Plus, Trash2, Sparkles, AlertTriangle, Stethoscope, Upload, FileText, History, RotateCcw, Printer, TrendingUp, TrendingDown, BellRing } from "lucide-react";
import { toast } from "sonner";
import AiLoader from "@/components/AiLoader";

const COMMON = ["Colesterolo Totale", "Colesterolo HDL", "Colesterolo LDL", "Trigliceridi", "Glicemia", "Emoglobina", "Ferritina", "TSH", "Vitamina D"];
const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const MEALS = ["Colazione", "Spuntino", "Pranzo", "Merenda", "Cena"];
const STATUS = {
  alto: "bg-destructive/15 text-destructive border-destructive/30",
  basso: "bg-clinical/15 text-clinical border-clinical/30",
  normale: "bg-primary/15 text-primary border-primary/30",
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", ".").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const sev = (s) => (s === "alto" || s === "basso" ? 1 : 0);

// Minimal inline SVG line chart for the printable multi-report PDF
const svgSpark = (points) => {
  const w = 460, h = 130, pad = 30;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = (max - min) || 1;
  const step = (w - 2 * pad) / Math.max(1, points.length - 1);
  const xy = points.map((p, i) => [pad + i * step, h - pad - ((p.value - min) / span) * (h - 2 * pad)]);
  const poly = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const dots = xy.map(([x, y], i) =>
    `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" fill="#2E5BFF"/>` +
    `<text x="${x.toFixed(1)}" y="${(y - 8).toFixed(1)}" font-size="10" text-anchor="middle" fill="#334">${points[i].value}</text>`).join("");
  const labels = points.map((p, i) =>
    `<text x="${(pad + i * step).toFixed(1)}" y="${h - 8}" font-size="9" text-anchor="middle" fill="#7C8E81">${(p.date || "").slice(5)}</text>`).join("");
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><polyline points="${poly}" fill="none" stroke="#2E5BFF" stroke-width="2"/>${dots}${labels}</svg>`;
};

const canon = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();

// Match a marker to its counterpart in the previous report, resilient to AI naming drift
const findPrevMarker = (name, prevMarkers) => {
  const c = canon(name);
  if (!c || !prevMarkers?.length) return null;
  let m = prevMarkers.find((p) => canon(p.marker) === c);
  if (m) return m;
  m = prevMarkers.find((p) => {
    const pc = canon(p.marker);
    return pc && (c.startsWith(pc + " ") || pc.startsWith(c + " "));
  });
  return m || null;
};

// Trend of a marker in the current report vs the previous one: 'peggiorato' | 'migliorato' | null
const markerTrend = (prevM, curM) => {
  if (!prevM || !curM) return null;
  const ps = prevM.status, cs = curM.status;
  if (sev(ps) < sev(cs)) return "peggiorato";        // normale -> fuori norma
  if (sev(ps) > sev(cs)) return "migliorato";        // fuori norma -> normale
  if (sev(ps) === 1 && sev(cs) === 1) {              // entrambi fuori norma
    if (ps !== cs) return "peggiorato";              // alto <-> basso
    const pv = toNum(prevM.value), cv = toNum(curM.value);
    if (pv === null || cv === null || pv === cv) return null;
    if (cs === "alto") return cv > pv ? "peggiorato" : "migliorato";
    if (cs === "basso") return cv < pv ? "peggiorato" : "migliorato";
  }
  return null;
};

export default function BloodTests() {
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [rows, setRows] = useState([{ marker: "", value: "", unit: "" }]);
  const [panelName, setPanelName] = useState("Analisi del sangue");
  const [docId, setDocId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rem, setRem] = useState({ blood_reminder_enabled: false, blood_reminder_months: 3, blood_reminder_smart: false, diet_autosync: true });
  const [savingRem, setSavingRem] = useState(false);
  const [marker, setMarker] = useState("");
  const [selected, setSelected] = useState({});
  const [weightLogs, setWeightLogs] = useState([]);
  const [applying, setApplying] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjText, setAdjText] = useState("");
  const [adjDays, setAdjDays] = useState([]);
  const [adjMeals, setAdjMeals] = useState([]);
  const fileRef = useRef(null);

  const openApply = (text) => {
    setAdjText(text); setAdjDays([]); setAdjMeals([]); setAdjOpen(true);
  };
  const toggleIn = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const confirmApply = async () => {
    setApplying(adjText);
    try {
      const { data } = await api.post("/diet/apply-adjustment", { adjustment: adjText, days: adjDays, meal_types: adjMeals });
      if (data.approved) { toast.success("Consiglio approvato: dieta aggiornata da DietFlix"); setAdjOpen(false); }
      else toast.error(`Non approvato da DietFlix: ${data.reason}`);
    } catch (err) {
      showApiError(err);
    } finally { setApplying(null); }
  };

  const load = async () => {
    setLoading(true);
    const [t, d, r, wl] = await Promise.all([
      api.get("/bloodtests"), api.get("/bloodtests/documents"), api.get("/blood-reminder-settings"), api.get("/weight"),
    ]);
    setTests(t.data.tests || []);
    setDocuments(d.data.documents || []);
    setRem(r.data);
    setWeightLogs(wl.data.logs || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      tests.forEach((t) => { if (!(t.test_id in next)) next[t.test_id] = true; });
      return next;
    });
  }, [tests]);

  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addRow = () => setRows((r) => [...r, { marker: "", value: "", unit: "" }]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const prefillFrom = (results, name, id) => {
    const clean = (results || []).map((r) => ({ marker: r.marker || "", value: String(r.value ?? ""), unit: r.unit || "" }));
    setRows(clean.length ? clean : [{ marker: "", value: "", unit: "" }]);
    if (name) setPanelName(name);
    setDocId(id || null);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Carica un file PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bloodtests/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      prefillFrom(data.results, data.panel_name, data.doc_id);
      setDocuments((d) => [{ doc_id: data.doc_id, filename: data.filename, panel_name: data.panel_name,
        date: data.date, results: data.results, uploaded_at: new Date().toISOString(), saved: false }, ...d]);
      toast.success(`Lette ${data.results?.length || 0} voci dal PDF. Controlla e modifica i valori.`);
    } catch (err) {
      showApiError(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    const valid = rows.filter((r) => r.marker && r.value);
    if (!valid.length) { toast.error("Inserisci almeno un valore"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bloodtests", { panel_name: panelName || "Analisi del sangue", results: valid, doc_id: docId });
      setTests((t) => [data, ...t]);
      setRows([{ marker: "", value: "", unit: "" }]);
      setDocId(null);
      toast.success("Analisi interpretate da DietFlix");
      if (data.diet_regenerating) toast.info("Valori cambiati: sto rigenerando la tua dieta in background…", { duration: 6000 });
      load();
    } catch (err) {
      showApiError(err);
    } finally { setSubmitting(false); }
  };

  const saveRem = async (next) => {
    setSavingRem(true);
    try {
      const { data } = await api.put("/blood-reminder-settings", next);
      setRem(data);
      toast.success(data.blood_reminder_enabled
        ? `Promemoria attivo: ogni ${data.blood_reminder_months} mesi`
        : "Promemoria analisi disattivato");
    } catch (err) {
      showApiError(err);
    } finally { setSavingRem(false); }
  };

  const exportReportPdf = (test) => {
    const it = test.interpretation || {};
    const w = window.open("", "_blank");
    if (!w) { toast.error("Consenti i popup per scaricare il PDF"); return; }
    const markersHtml = (it.markers || []).map((m) => (
      `<tr><td>${m.marker || ""}</td><td class="v">${m.value ?? ""} ${m.unit || ""}</td>` +
      `<td class="s ${m.status || ""}">${m.status || ""}</td><td class="r">${m.reference || ""}</td>` +
      `<td class="c">${m.comment || ""}</td></tr>`
    )).join("");
    const list = (arr) => (arr && arr.length) ? `<ul>${arr.map((x) => `<li>${x}</li>`).join("")}</ul>` : "";
    w.document.write(
      `<html><head><title>Referto — ${test.panel_name || "Analisi"} — DietFlix</title><style>
      *{font-family:Arial,Helvetica,sans-serif;color:#1F2923}
      body{margin:32px;max-width:820px}
      h1{color:#2D5A43;font-size:22px;margin-bottom:2px}
      .sub{color:#7C8E81;margin-top:0;font-size:13px}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th,td{text-align:left;padding:7px 8px;border-bottom:1px solid #E2DDD3;font-size:13px;vertical-align:top}
      th{color:#2D5A43;font-size:12px;text-transform:uppercase;letter-spacing:.03em}
      .v{font-family:monospace}
      .s{font-weight:bold;text-transform:capitalize}
      .s.alto{color:#B91C1C}.s.basso{color:#1D4ED8}.s.normale{color:#2D5A43}
      .r{color:#526356}.c{color:#526356}
      .box{border:1px solid #E2DDD3;border-radius:10px;padding:12px 16px;margin:10px 0}
      .box h3{margin:0 0 6px;font-size:14px;color:#2D5A43}
      .warn{border-color:#F1B4B4;background:#FCF2F2}
      .disc{color:#7C8E81;font-size:12px;font-style:italic;border-top:1px solid #E2DDD3;padding-top:10px;margin-top:14px}
      ul{margin:4px 0 0 18px;font-size:13px;color:#526356}
      </style></head><body>
      <h1>DietFlix — Referto interpretato</h1>
      <p class="sub">${test.panel_name || "Analisi del sangue"} · Data: ${test.date || ""}</p>
      <table><thead><tr><th>Marcatore</th><th>Valore</th><th>Stato</th><th>Riferimento</th><th>Nota</th></tr></thead>
      <tbody>${markersHtml}</tbody></table>
      ${it.overall ? `<div class="box"><h3>Valutazione complessiva</h3><p style="font-size:13px;margin:0">${it.overall}</p></div>` : ""}
      ${(it.nutrition_advice && it.nutrition_advice.length) ? `<div class="box"><h3>Consigli nutrizionali</h3>${list(it.nutrition_advice)}</div>` : ""}
      ${(it.warnings && it.warnings.length) ? `<div class="box warn"><h3>Attenzione</h3>${list(it.warnings)}</div>` : ""}
      ${it.disclaimer ? `<p class="disc">${it.disclaimer}</p>` : ""}
      <p class="disc">Documento generato da DietFlix a scopo informativo — da condividere con il proprio medico.</p>
      </body></html>`
    );
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  const exportMultiPdf = () => {
    const chosen = tests.filter((t) => selected[t.test_id]);
    if (!chosen.length) { toast.error("Seleziona almeno un referto"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Consenti i popup per scaricare il PDF"); return; }
    const asc = [...chosen].filter((t) => t.date).sort((a, b) => a.date.localeCompare(b.date));
    const byMarker = {};
    asc.forEach((t) => (t.results || []).forEach((r) => {
      const n = toNum(r.value);
      if (n === null || !r.marker) return;
      (byMarker[r.marker] = byMarker[r.marker] || []).push({ date: t.date, value: n, unit: r.unit || "" });
    }));
    const trendMarkers = Object.keys(byMarker).filter((m) => byMarker[m].length >= 2).sort();
    const trendHtml = trendMarkers.length
      ? `<h2 class="sec">Andamento nel tempo</h2>` + trendMarkers.map((m) =>
          `<div class="chart"><h3>${m}${byMarker[m][0].unit ? ` (${byMarker[m][0].unit})` : ""}</h3>${svgSpark(byMarker[m])}</div>`).join("")
      : "";
    const wpts = (weightLogs || []).filter((l) => toNum(l.weight_kg) !== null && l.date)
      .map((l) => ({ date: l.date, value: toNum(l.weight_kg) }))
      .sort((a, b) => a.date.localeCompare(b.date));
    const weightHtml = wpts.length >= 2
      ? `<h2 class="sec">Andamento del peso</h2><div class="chart"><h3>Peso (kg)</h3>${svgSpark(wpts)}</div>`
      : "";
    const reportsHtml = asc.map((t) => {
      const it = t.interpretation || {};
      const rows = (it.markers || []).map((m) =>
        `<tr><td>${m.marker || ""}</td><td class="v">${m.value ?? ""} ${m.unit || ""}</td>` +
        `<td class="s ${m.status || ""}">${m.status || ""}${m.severity === "critico" ? " (critico)" : ""}</td><td class="r">${m.reference || ""}</td></tr>`).join("");
      return `<section class="rep"><h2>${t.panel_name || "Analisi"} — ${t.date || ""}</h2>` +
        `<table><thead><tr><th>Marcatore</th><th>Valore</th><th>Stato</th><th>Rif.</th></tr></thead><tbody>${rows}</tbody></table>` +
        (it.overall ? `<p class="ov"><strong>Valutazione:</strong> ${it.overall}</p>` : "") +
        (it.see_doctor ? `<p class="doc"><strong>Consulta il medico:</strong> ${it.doctor_advice || ""}</p>` : "") +
        `</section>`;
    }).join("");
    w.document.write(
      `<html><head><title>Storico analisi — DietFlix</title><style>
      *{font-family:Arial,Helvetica,sans-serif;color:#1F2923}
      body{margin:32px;max-width:820px}
      h1{color:#2E5BFF;font-size:22px;margin-bottom:2px}
      .sub{color:#7C8E81;margin-top:0;font-size:13px}
      .sec{color:#2E5BFF;font-size:16px;border-bottom:2px solid #E2DDF3;padding-bottom:4px;margin:22px 0 10px}
      .chart{break-inside:avoid;margin:8px 0 16px}.chart h3{font-size:13px;margin:0 0 2px;color:#334}
      .rep{break-inside:avoid;margin:16px 0;border:1px solid #E2DDF3;border-radius:10px;padding:12px 16px}
      .rep h2{font-size:15px;color:#2E5BFF;margin:0 0 8px}
      table{width:100%;border-collapse:collapse;margin:6px 0}
      th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #Eee;font-size:12px}
      th{color:#2E5BFF;font-size:11px;text-transform:uppercase}
      .v{font-family:monospace}.s{font-weight:bold;text-transform:capitalize}
      .s.alto{color:#B91C1C}.s.basso{color:#1D4ED8}.s.normale{color:#15803D}
      .r{color:#526356}.ov{font-size:12px;color:#334;margin:6px 0 0}
      .doc{font-size:12px;color:#B91C1C;margin:4px 0 0}
      .disc{color:#7C8E81;font-size:11px;font-style:italic;border-top:1px solid #E2DDF3;padding-top:10px;margin-top:16px}
      </style></head><body>
      <h1>DietFlix — Storico delle analisi</h1>
      <p class="sub">${asc.length} referti · documento pronto per la visita medica</p>
      ${trendHtml}
      ${weightHtml}
      <h2 class="sec">Referti</h2>
      ${reportsHtml}
      <p class="disc">Documento generato da DietFlix a scopo informativo — non sostituisce il parere del medico.</p>
      </body></html>`
    );
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };
  const compare = useMemo(() => {
    const sorted = [...tests].filter((t) => t.date).sort((a, b) => a.date.localeCompare(b.date));
    const byMarker = {};
    sorted.forEach((t) => {
      (t.results || []).forEach((r) => {
        const n = toNum(r.value);
        if (n === null || !r.marker) return;
        (byMarker[r.marker] = byMarker[r.marker] || []).push({ date: t.date, value: n, unit: r.unit || "" });
      });
    });
    const markers = Object.keys(byMarker).filter((m) => byMarker[m].length >= 2).sort();
    return { byMarker, markers };
  }, [tests]);

  useEffect(() => {
    if (compare.markers.length && !compare.markers.includes(marker)) setMarker(compare.markers[0]);
  }, [compare.markers, marker]);

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  const chartData = marker ? (compare.byMarker[marker] || []) : [];
  const chartUnit = chartData[0]?.unit || "";

  return (
    <div className="space-y-6" data-testid="blood-section">
      {(uploading || submitting) && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-background/70 backdrop-blur-sm" data-testid="blood-ai-overlay">
          <div className="rounded-3xl bg-card border shadow-2xl">
            <AiLoader type="blood" title={uploading ? "DietFlix sta leggendo il tuo referto" : "DietFlix sta interpretando le tue analisi"} />
          </div>
        </div>
      )}
      <div>
        <h2 className="font-heading text-2xl font-semibold">Analisi del sangue</h2>
        <p className="text-muted-foreground text-sm">Carica il PDF del referto: DietFlix estrae i valori. Puoi sempre correggerli a mano prima di salvarli.</p>
      </div>

      {/* Upload PDF */}
      <Card className="rounded-2xl border-dashed border-clinical/40 bg-clinical/5">
        <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-clinical/15 grid place-items-center shrink-0"><Upload className="h-6 w-6 text-clinical" /></div>
          <div className="flex-1">
            <div className="font-medium">Carica il referto PDF</div>
            <p className="text-sm text-muted-foreground">Estrazione automatica dei valori da parte di DietFlix (max 10 MB).</p>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="blood-pdf-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="blood-pdf-upload-button">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? "Lettura in corso…" : "Scegli PDF"}
          </Button>
        </CardContent>
      </Card>

      {/* Manual / editable rows */}
      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TestTube className="h-5 w-5 text-clinical" /> Valori del referto</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Nome pannello</Label>
            <Input value={panelName} onChange={(e) => setPanelName(e.target.value)} data-testid="blood-panel-name" />
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`blood-row-${i}`}>
              <div className="col-span-6 sm:col-span-5 space-y-1">
                {i === 0 && <Label className="text-xs">Marcatore</Label>}
                <Input list="markers" value={row.marker} onChange={(e) => setRow(i, "marker", e.target.value)}
                  placeholder="es. Glicemia" data-testid={`blood-marker-${i}`} />
              </div>
              <div className="col-span-3 sm:col-span-4 space-y-1">
                {i === 0 && <Label className="text-xs">Valore</Label>}
                <Input value={row.value} onChange={(e) => setRow(i, "value", e.target.value)} placeholder="es. 95" data-testid={`blood-value-${i}`} />
              </div>
              <div className="col-span-2 space-y-1">
                {i === 0 && <Label className="text-xs">Unità</Label>}
                <Input value={row.unit} onChange={(e) => setRow(i, "unit", e.target.value)} placeholder="mg/dL" data-testid={`blood-unit-${i}`} />
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="icon" onClick={() => delRow(i)} disabled={rows.length === 1} data-testid={`blood-del-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <datalist id="markers">{COMMON.map((m) => <option key={m} value={m} />)}</datalist>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" onClick={addRow} data-testid="add-blood-row-button"><Plus className="h-4 w-4 mr-2" /> Aggiungi valore</Button>
            <Button onClick={submit} disabled={submitting} data-testid="submit-blood-button">
              <Sparkles className="h-4 w-4 mr-2" /> {submitting ? "Analisi in corso…" : "Fai interpretare a DietFlix"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reminder settings */}
      <Card className="rounded-2xl" data-testid="blood-reminder-card">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><BellRing className="h-5 w-5 text-accent" /> Promemoria analisi</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Ricordami di aggiornare le analisi</div>
              <p className="text-sm text-muted-foreground">Ti inviamo un'email quando è ora di ricaricare un referto.</p>
            </div>
            <Switch checked={rem.blood_reminder_enabled} disabled={savingRem}
              onCheckedChange={(v) => saveRem({ ...rem, blood_reminder_enabled: v })}
              data-testid="blood-reminder-switch" />
          </div>
          <div className="max-w-xs" style={{ opacity: rem.blood_reminder_enabled ? 1 : 0.5 }}>
            <Label className="text-xs">Frequenza</Label>
            <Select value={String(rem.blood_reminder_months)} disabled={!rem.blood_reminder_enabled || savingRem}
              onValueChange={(v) => saveRem({ ...rem, blood_reminder_months: parseInt(v, 10) })}>
              <SelectTrigger data-testid="blood-reminder-months"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Ogni 3 mesi</SelectItem>
                <SelectItem value="6">Ogni 6 mesi</SelectItem>
                <SelectItem value="12">Ogni 12 mesi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <div className="font-medium">Promemoria intelligente</div>
              <p className="text-sm text-muted-foreground">Se ci sono valori fuori norma, ti avvisiamo ogni mese.</p>
            </div>
            <Switch checked={rem.blood_reminder_smart} disabled={savingRem}
              onCheckedChange={(v) => saveRem({ ...rem, blood_reminder_smart: v })}
              data-testid="blood-reminder-smart-switch" />
          </div>
          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <div className="font-medium">Sincronizza dieta con le analisi</div>
              <p className="text-sm text-muted-foreground">Rigenera automaticamente la dieta quando carichi un referto con valori cambiati (Premium).</p>
            </div>
            <Switch checked={rem.diet_autosync} disabled={savingRem}
              onCheckedChange={(v) => saveRem({ ...rem, diet_autosync: v })}
              data-testid="diet-autosync-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Comparison chart */}
      {compare.markers.length > 0 && (
        <Card className="rounded-2xl" data-testid="blood-compare-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-clinical" /> Confronto nel tempo</span>
              <div className="w-44">
                <Select value={marker} onValueChange={setMarker}>
                  <SelectTrigger data-testid="blood-compare-marker"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {compare.markers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full" data-testid="blood-compare-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip formatter={(v) => [`${v} ${chartUnit}`, marker]} />
                  <Line type="monotone" dataKey="value" stroke="#2D5A43" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Andamento di <strong>{marker}</strong>{chartUnit ? ` (${chartUnit})` : ""} sui referti salvati.</p>
          </CardContent>
        </Card>
      )}

      {/* Document history */}
      {documents.length > 0 && (
        <Card className="rounded-2xl" data-testid="blood-documents-card">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><History className="h-5 w-5 text-clinical" /> Cronologia documenti caricati</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.doc_id} className="flex items-center gap-3 rounded-xl border p-3" data-testid={`blood-doc-${doc.doc_id}`}>
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{doc.filename}</div>
                  <div className="text-xs text-muted-foreground font-mono-data">
                    {(doc.uploaded_at || "").slice(0, 10)} · {(doc.results || []).length} valori{doc.saved ? " · salvato" : ""}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { prefillFrom(doc.results, doc.panel_name, doc.doc_id); toast.info("Valori ricaricati nell'editor"); }}
                  data-testid={`blood-doc-reload-${doc.doc_id}`}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Ricarica
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tests.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="blood-multi-toolbar">
          <p className="text-sm text-muted-foreground">Seleziona i referti e crea un unico PDF con i grafici di andamento, pronto per la visita.</p>
          <Button variant="outline" onClick={exportMultiPdf} data-testid="export-multi-button">
            <Printer className="h-4 w-4 mr-2" /> Esporta referto multiplo ({tests.filter((t) => selected[t.test_id]).length})
          </Button>
        </div>
      )}

      {tests.map((test, idx) => {
        const it = test.interpretation || {};
        const prevIt = tests[idx + 1]?.interpretation || {};
        const prevMarkers = prevIt.markers || [];
        const worsened = (it.markers || []).filter((m) => markerTrend(findPrevMarker(m.marker, prevMarkers), m) === "peggiorato").map((m) => m.marker);
        return (
          <Card key={test.test_id} className="rounded-2xl" data-testid={`blood-test-${test.test_id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Checkbox checked={!!selected[test.test_id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [test.test_id]: !!v }))}
                    data-testid={`blood-select-${test.test_id}`} />
                  <Stethoscope className="h-5 w-5 text-clinical" /> {test.panel_name}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-mono-data font-normal">{test.date}</span>
                  <Button size="sm" variant="outline" onClick={() => exportReportPdf(test)} data-testid={`export-report-${test.test_id}`}>
                    <Printer className="h-4 w-4 mr-1" /> Esporta PDF
                  </Button>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(it.see_doctor || (it.markers || []).some((m) => m.severity === "critico")) && (
                <div className="rounded-xl border-2 border-destructive/50 bg-destructive/10 p-4 flex items-start gap-3" data-testid={`see-doctor-${test.test_id}`}>
                  <Stethoscope className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-destructive">Ti consigliamo di consultare il medico</div>
                    <p className="text-sm text-muted-foreground">{it.doctor_advice || "Alcuni valori risultano fuori norma in modo rilevante. Porta questo referto al tuo medico per una valutazione."}</p>
                  </div>
                </div>
              )}
              {worsened.length > 0 && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 flex items-start gap-2" data-testid={`blood-alert-${test.test_id}`}>
                  <TrendingDown className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm"><span className="font-medium text-destructive">Peggiorati rispetto al referto precedente:</span> {worsened.join(", ")}</p>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                {(it.markers || []).map((m, i) => {
                  const trend = markerTrend(findPrevMarker(m.marker, prevMarkers), m);
                  return (
                  <div key={i} className={`rounded-xl border p-3 ${STATUS[m.status] || "bg-secondary"} ${m.severity === "critico" ? "ring-2 ring-destructive/50" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{m.marker}</span>
                      <span className="flex items-center gap-1">
                        {trend === "peggiorato" && <Badge className="bg-destructive text-destructive-foreground border-destructive gap-1"><TrendingDown className="h-3 w-3" /> Peggiorato</Badge>}
                        {trend === "migliorato" && <Badge className="bg-primary text-primary-foreground border-primary gap-1"><TrendingUp className="h-3 w-3" /> Migliorato</Badge>}
                        <Badge variant="outline" className="font-mono-data capitalize">{m.status}</Badge>
                      </span>
                    </div>
                    <div className="font-mono-data text-lg">{m.value} {m.unit}</div>
                    <div className="text-xs opacity-80">Rif: {m.reference}</div>
                    {m.comment && <p className="text-xs mt-1 opacity-90">{m.comment}</p>}
                  </div>
                  );
                })}
              </div>
              {it.overall && (
                <div className="rounded-xl bg-clinical/5 border border-clinical/20 p-4">
                  <div className="font-medium mb-1 text-clinical">Valutazione complessiva</div>
                  <p className="text-sm text-muted-foreground">{it.overall}</p>
                </div>
              )}
              {it.nutrition_advice?.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Consigli nutrizionali</div>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {it.nutrition_advice.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {it.diet_adjustments?.length > 0 && (
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-4" data-testid={`diet-adjustments-${test.test_id}`}>
                  <div className="font-medium mb-2 text-accent flex items-center gap-2">
                    <Sparkles className="h-4 w-4" /> Consigli su misura per la dieta{test.has_worsened ? " · valori peggiorati" : ""}
                  </div>
                  <div className="space-y-2">
                    {it.diet_adjustments.map((a, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-muted-foreground">• {a}</span>
                        <Button size="sm" variant="outline" className="shrink-0" onClick={() => openApply(a)}
                          disabled={applying !== null} data-testid={`apply-adjustment-${test.test_id}-${i}`}>
                          <Sparkles className="h-3.5 w-3.5 mr-1" /> {applying === a ? "Applico…" : "Applica alla dieta"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {it.warnings?.length > 0 && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
                  <div className="font-medium mb-1 text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Attenzione</div>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {it.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {it.disclaimer && <p className="text-xs text-muted-foreground italic border-t pt-3">{it.disclaimer}</p>}
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="apply-adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Applica il consiglio</DialogTitle>
            <DialogDescription className="text-sm">{adjText}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Giorni</Label>
                <span className="text-xs text-muted-foreground">{adjDays.length === 0 ? "Tutti i giorni" : `${adjDays.length} selezionati`}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const on = adjDays.includes(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleIn(adjDays, setAdjDays, d)}
                      data-testid={`adj-day-${d}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/50"}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Pasti</Label>
                <span className="text-xs text-muted-foreground">{adjMeals.length === 0 ? "Tutti i pasti" : `${adjMeals.length} selezionati`}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {MEALS.map((m) => {
                  const on = adjMeals.includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleIn(adjMeals, setAdjMeals, m)}
                      data-testid={`adj-meal-${m}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-accent text-accent-foreground border-accent" : "bg-background border-border text-muted-foreground hover:border-accent/50"}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Se non selezioni nulla, il consiglio verrà applicato a tutti i giorni e a tutti i pasti. La modifica viene comunque valutata e approvata da DietFlix.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)} data-testid="adj-cancel">Annulla</Button>
            <Button onClick={confirmApply} disabled={applying !== null} data-testid="adj-confirm">
              <Sparkles className="h-4 w-4 mr-1" /> {applying !== null ? "Applico…" : "Applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
